import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../contexts/UserContext';
import {
  Plus, Edit2, Trash2, Copy, ClipboardList, BarChart3,
  Users, CheckCircle, Clock, X, Image, Upload, Search,
  PlayCircle, ChevronRight, AlertTriangle, Calendar
} from 'lucide-react';

// Stat Card Component (OEM Style)
const StatCard = ({ icon: Icon, value, label, badge, badgeColor, iconBg, onClick }) => (
  <div
    onClick={onClick}
    className={`bg-white rounded-2xl p-4 border border-slate-100 shadow-sm ${onClick ? 'cursor-pointer hover:shadow-md transition-all' : ''}`}
  >
    <div className="flex items-center justify-between mb-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${iconBg || 'bg-slate-100'}`}>
        <Icon className="h-5 w-5 text-slate-600" />
      </div>
      {badge && (
        <span className={`px-2 py-0.5 text-[9px] font-black uppercase tracking-wider rounded-md ${badgeColor || 'bg-slate-100 text-slate-600'}`}>
          {badge}
        </span>
      )}
    </div>
    <p className="text-2xl font-black text-slate-900">{value}</p>
    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">{label}</p>
  </div>
);

// Survey Card Component (OEM Style)
const SurveyCard = ({ survey, isAdmin, onEdit, onAssign, onResults, onDuplicate, onDelete, onFill, formatDate }) => (
  <div className={`bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden transition-all hover:shadow-md ${!survey.is_active ? 'opacity-60' : ''}`}>
    {/* Cover Image */}
    {survey.image_url ? (
      <div className="h-40 overflow-hidden bg-slate-100">
        <img src={survey.image_url} alt={survey.title} className="w-full h-full object-cover" />
      </div>
    ) : (
      <div className="h-40 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
        <Image className="h-10 w-10 text-slate-300" />
      </div>
    )}

    <div className="p-4 space-y-3">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-sm font-black text-slate-900 line-clamp-2">{survey.title}</h3>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide rounded-md flex-shrink-0 ${
          survey.is_active
            ? 'bg-emerald-50 text-emerald-600'
            : 'bg-slate-100 text-slate-500'
        }`}>
          <span className={`w-1 h-1 rounded-full ${survey.is_active ? 'bg-emerald-500' : 'bg-slate-400'}`} />
          {survey.is_active ? 'Active' : 'Inactive'}
        </span>
      </div>

      {/* Description */}
      <p className="text-xs text-slate-500 line-clamp-2">{survey.description || 'No description'}</p>

      {/* Meta */}
      <div className="flex flex-wrap gap-2">
        {isAdmin && (
          <>
            <div className="flex items-center gap-1 px-2 py-1 bg-slate-50 rounded-lg">
              <Users size={12} className="text-slate-400" />
              <span className="text-[10px] font-bold text-slate-600">{survey.assignment_count || 0} assigned</span>
            </div>
            <div className="flex items-center gap-1 px-2 py-1 bg-emerald-50 rounded-lg">
              <CheckCircle size={12} className="text-emerald-500" />
              <span className="text-[10px] font-bold text-emerald-600">{survey.response_count || 0} responses</span>
            </div>
          </>
        )}
        {survey.deadline && (
          <div className="flex items-center gap-1 px-2 py-1 bg-amber-50 rounded-lg">
            <Calendar size={12} className="text-amber-500" />
            <span className="text-[10px] font-bold text-amber-600">Due: {formatDate(survey.deadline)}</span>
          </div>
        )}
      </div>

      {/* Badges */}
      <div className="flex flex-wrap gap-1.5">
        {survey.is_anonymous && (
          <span className="px-2 py-0.5 text-[9px] font-bold bg-violet-100 text-violet-600 rounded-md uppercase">Anonymous</span>
        )}
        <span className="px-2 py-0.5 text-[9px] font-bold bg-slate-100 text-slate-600 rounded-md uppercase">{survey.question_count || 0} questions</span>
        {!isAdmin && survey.is_completed && (
          <span className="px-2 py-0.5 text-[9px] font-bold bg-emerald-100 text-emerald-600 rounded-md uppercase">Completed</span>
        )}
        {!isAdmin && !survey.is_completed && (
          <span className="px-2 py-0.5 text-[9px] font-bold bg-amber-100 text-amber-600 rounded-md uppercase">Pending</span>
        )}
      </div>

      {/* Actions */}
      <div className="pt-3 border-t border-slate-100 flex items-center gap-1.5">
        {isAdmin ? (
          <>
            <button
              onClick={() => onEdit(survey)}
              className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors"
              title="Edit Survey"
            >
              <Edit2 size={14} />
            </button>
            <button
              onClick={() => onAssign(survey)}
              className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-orange-50 hover:text-orange-600 hover:border-orange-200 transition-colors"
              title="Assign Users"
            >
              <Users size={14} />
            </button>
            <button
              onClick={() => onResults(survey)}
              className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-violet-50 hover:text-violet-600 hover:border-violet-200 transition-colors"
              title="View Results"
            >
              <BarChart3 size={14} />
            </button>
            <button
              onClick={() => onDuplicate(survey)}
              className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 transition-colors"
              title="Duplicate"
            >
              <Copy size={14} />
            </button>
            <button
              onClick={() => onDelete(survey)}
              className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors"
              title="Delete"
            >
              <Trash2 size={14} />
            </button>
          </>
        ) : (
          <button
            onClick={() => onFill(survey)}
            disabled={survey.is_completed}
            className={`flex-1 h-9 flex items-center justify-center gap-1.5 rounded-xl text-xs font-bold uppercase tracking-wide transition-all ${
              survey.is_completed
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                : 'bg-orange-500 text-white hover:bg-orange-600 shadow-lg shadow-orange-500/20'
            }`}
          >
            {survey.is_completed ? (
              <>
                <CheckCircle size={14} />
                Completed
              </>
            ) : (
              <>
                <PlayCircle size={14} />
                Fill Survey
              </>
            )}
          </button>
        )}
      </div>

      {/* Footer */}
      <div className="pt-2 border-t border-slate-100">
        <p className="text-[10px] text-slate-400">
          {isAdmin
            ? `Created ${formatDate(survey.created_at)} by ${survey.created_by_name}`
            : `Assigned ${formatDate(survey.assigned_at)}`
          }
        </p>
      </div>
    </div>
  </div>
);

const SurveyList = () => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [newSurvey, setNewSurvey] = useState({
    title: '',
    description: '',
    is_anonymous: false,
    deadline: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);

  const isCompanyAdmin = user?.isCompanyAdmin || user?.isSuperuser || user?.isStaff;

  useEffect(() => {
    fetchSurveys();
    if (isCompanyAdmin) {
      fetchUsers();
    }
  }, [isCompanyAdmin]);

  const showToastNotification = (message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const fetchSurveys = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const endpoint = isCompanyAdmin
        ? `${process.env.REACT_APP_API_URL}/api/surveys/`
        : `${process.env.REACT_APP_API_URL}/api/surveys/my-assigned/`;
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSurveys(response.data || []);
    } catch (error) {
      console.error('Error fetching surveys:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/users/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAvailableUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchAssignments = async (surveyId) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/surveys/${surveyId}/assignments/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAssignments(response.data || []);
    } catch (error) {
      console.error('Error fetching assignments:', error);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const handleCreateSurvey = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const formData = new FormData();
      formData.append('title', newSurvey.title);
      formData.append('description', newSurvey.description || '');
      formData.append('is_anonymous', newSurvey.is_anonymous ? 'true' : 'false');
      if (newSurvey.deadline) {
        formData.append('deadline', newSurvey.deadline);
      }
      if (imageFile) {
        formData.append('image', imageFile);
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/surveys/`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      showToastNotification('Survey created successfully');
      setShowCreateModal(false);
      setNewSurvey({ title: '', description: '', is_anonymous: false, deadline: '' });
      setImageFile(null);
      setImagePreview(null);
      navigate(`/surveys/${response.data.id}/builder`);
    } catch (error) {
      console.error('Error creating survey:', error);
      showToastNotification('Error creating survey');
    }
  };

  const handleDeleteSurvey = async () => {
    if (!selectedSurvey) return;
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/surveys/${selectedSurvey.id}/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToastNotification('Survey deleted successfully');
      setShowDeleteModal(false);
      setSelectedSurvey(null);
      fetchSurveys();
    } catch (error) {
      console.error('Error deleting survey:', error);
      showToastNotification('Error deleting survey');
    }
  };

  const handleDuplicateSurvey = async (survey) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/surveys/${survey.id}/duplicate/`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToastNotification('Survey duplicated successfully');
      fetchSurveys();
    } catch (error) {
      console.error('Error duplicating survey:', error);
      showToastNotification('Error duplicating survey');
    }
  };

  const handleAssignUsers = async () => {
    if (!selectedSurvey || selectedUserIds.length === 0) return;
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/surveys/${selectedSurvey.id}/assignments/`,
        { user_ids: selectedUserIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToastNotification(`Assigned ${selectedUserIds.length} users`);
      setSelectedUserIds([]);
      fetchAssignments(selectedSurvey.id);
      fetchSurveys();
    } catch (error) {
      console.error('Error assigning users:', error);
      showToastNotification('Error assigning users');
    }
  };

  const handleRemoveAssignment = async (assignmentId) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/surveys/${selectedSurvey.id}/assignments/${assignmentId}/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToastNotification('Assignment removed');
      fetchAssignments(selectedSurvey.id);
      fetchSurveys();
    } catch (error) {
      console.error('Error removing assignment:', error);
    }
  };

  const openAssignModal = (survey) => {
    setSelectedSurvey(survey);
    setSelectedUserIds([]);
    fetchAssignments(survey.id);
    setShowAssignModal(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Stats calculations
  const totalSurveys = surveys.length;
  const activeSurveys = surveys.filter(s => s.is_active).length;
  const totalResponses = surveys.reduce((acc, s) => acc + (s.response_count || 0), 0);
  const pendingSurveys = isCompanyAdmin
    ? surveys.filter(s => (s.assignment_count || 0) > (s.response_count || 0)).length
    : surveys.filter(s => !s.is_completed).length;

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl p-10 flex items-center justify-center shadow-sm border border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-slate-600 text-sm font-medium">Loading surveys...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5">
      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-3">
        <StatCard
          icon={ClipboardList}
          value={totalSurveys}
          label="Total Surveys"
          badge={activeSurveys > 0 ? `${activeSurveys} active` : null}
          badgeColor="bg-emerald-100 text-emerald-600"
          iconBg="bg-orange-100"
        />
        <StatCard
          icon={Users}
          value={isCompanyAdmin ? surveys.reduce((acc, s) => acc + (s.assignment_count || 0), 0) : totalSurveys}
          label={isCompanyAdmin ? "Assigned" : "Assigned to You"}
          iconBg="bg-violet-100"
        />
        <StatCard
          icon={CheckCircle}
          value={isCompanyAdmin ? totalResponses : surveys.filter(s => s.is_completed).length}
          label={isCompanyAdmin ? "Responses" : "Completed"}
          iconBg="bg-emerald-100"
        />
        <StatCard
          icon={Clock}
          value={pendingSurveys}
          label="Pending"
          badge={pendingSurveys > 0 ? 'ACTION' : null}
          badgeColor="bg-amber-100 text-amber-600"
          iconBg="bg-amber-100"
        />
      </div>

      {/* Action Button */}
      {isCompanyAdmin && (
        <div className="flex items-center justify-end">
          <button
            onClick={() => setShowCreateModal(true)}
            className="h-9 px-4 bg-orange-500 text-white rounded-xl hover:bg-orange-600 flex items-center gap-1.5 font-bold uppercase tracking-wide text-[10px] transition-all shadow-lg shadow-orange-500/20"
          >
            <Plus className="h-3.5 w-3.5" />
            New Survey
          </button>
        </div>
      )}

      {/* Survey Grid */}
      {surveys.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-10 text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-4">
            <ClipboardList className="h-8 w-8 text-slate-400" />
          </div>
          <p className="text-sm font-black text-slate-900 mb-1">
            {isCompanyAdmin ? 'No surveys yet' : 'No surveys assigned to you'}
          </p>
          <p className="text-xs text-slate-400 mb-4">
            {isCompanyAdmin ? 'Create your first survey to get started' : 'Check back later for new surveys'}
          </p>
          {isCompanyAdmin && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="text-[10px] font-bold text-orange-500 hover:text-orange-600 uppercase tracking-wide"
            >
              Create your first survey →
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {surveys.map(survey => (
            <SurveyCard
              key={survey.id}
              survey={survey}
              isAdmin={isCompanyAdmin}
              onEdit={(s) => navigate(`/surveys/${s.id}/builder`)}
              onAssign={openAssignModal}
              onResults={(s) => navigate(`/surveys/${s.id}/results`)}
              onDuplicate={handleDuplicateSurvey}
              onDelete={(s) => { setSelectedSurvey(s); setShowDeleteModal(true); }}
              onFill={(s) => navigate(`/surveys/${s.id}/fill`)}
              formatDate={formatDate}
            />
          ))}
        </div>
      )}

      {/* Create Survey Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowCreateModal(false)} />
          <div className="relative bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
                  <Plus className="h-4 w-4 text-orange-500" />
                </div>
                <h2 className="text-sm font-black text-slate-900 uppercase">Create Survey</h2>
              </div>
              <button onClick={() => setShowCreateModal(false)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
                <X size={18} />
              </button>
            </div>

            <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
              {/* Title */}
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Title *</label>
                <input
                  type="text"
                  value={newSurvey.title}
                  onChange={e => setNewSurvey({ ...newSurvey, title: e.target.value })}
                  placeholder="Survey title"
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Description</label>
                <textarea
                  value={newSurvey.description}
                  onChange={e => setNewSurvey({ ...newSurvey, description: e.target.value })}
                  placeholder="Survey description"
                  rows={3}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
                />
              </div>

              {/* Deadline */}
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Deadline</label>
                <input
                  type="datetime-local"
                  value={newSurvey.deadline}
                  onChange={e => setNewSurvey({ ...newSurvey, deadline: e.target.value })}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>

              {/* Anonymous */}
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newSurvey.is_anonymous}
                    onChange={e => setNewSurvey({ ...newSurvey, is_anonymous: e.target.checked })}
                    className="w-4 h-4 text-orange-500 border-slate-300 rounded focus:ring-orange-500"
                  />
                  <span className="text-sm font-bold text-slate-700">Anonymous responses</span>
                </label>
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Cover Image</label>
                {imagePreview ? (
                  <div className="relative rounded-xl overflow-hidden border border-slate-200">
                    <img src={imagePreview} alt="Preview" className="w-full h-32 object-cover" />
                    <button
                      onClick={handleRemoveImage}
                      className="absolute top-2 right-2 p-1.5 bg-black/50 rounded-lg text-white hover:bg-red-500 transition-colors"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-slate-200 rounded-xl cursor-pointer hover:border-orange-300 hover:bg-orange-50/50 transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                    <Upload className="h-6 w-6 text-slate-400 mb-2" />
                    <span className="text-xs font-bold text-slate-600">Click to upload</span>
                    <span className="text-[10px] text-slate-400">PNG, JPG up to 5MB</span>
                  </label>
                )}
              </div>
            </div>

            <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 flex items-center justify-end gap-2">
              <button
                onClick={() => { setShowCreateModal(false); setImageFile(null); setImagePreview(null); }}
                className="h-9 px-4 text-slate-600 rounded-xl hover:bg-slate-200 font-bold text-xs transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateSurvey}
                disabled={!newSurvey.title}
                className="h-9 px-4 bg-orange-500 text-white rounded-xl hover:bg-orange-600 disabled:bg-slate-200 disabled:text-slate-400 font-bold text-xs uppercase tracking-wide transition-all shadow-lg shadow-orange-500/20 disabled:shadow-none"
              >
                Create & Add Questions
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && selectedSurvey && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowDeleteModal(false)} />
          <div className="relative bg-white rounded-2xl w-full max-w-sm shadow-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                  <Trash2 className="h-4 w-4 text-red-500" />
                </div>
                <h2 className="text-sm font-black text-slate-900 uppercase">Delete Survey</h2>
              </div>
              <button onClick={() => setShowDeleteModal(false)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
                <X size={18} />
              </button>
            </div>

            <div className="p-4">
              <p className="text-sm text-slate-700 mb-2">Are you sure you want to delete "{selectedSurvey.title}"?</p>
              <div className="p-3 bg-red-50 border border-red-100 rounded-xl flex items-center gap-2">
                <AlertTriangle size={16} className="text-red-500 flex-shrink-0" />
                <span className="text-xs font-bold text-red-700">This will delete all questions and responses.</span>
              </div>
            </div>

            <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 flex items-center justify-end gap-2">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="h-9 px-4 text-slate-600 rounded-xl hover:bg-slate-200 font-bold text-xs transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteSurvey}
                className="h-9 px-4 bg-red-500 text-white rounded-xl hover:bg-red-600 font-bold text-xs uppercase tracking-wide transition-all"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Users Modal */}
      {showAssignModal && selectedSurvey && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => { setShowAssignModal(false); setUserSearchQuery(''); }} />
          <div className="relative bg-white rounded-2xl w-full max-w-lg shadow-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
                  <Users className="h-4 w-4 text-orange-500" />
                </div>
                <div>
                  <h2 className="text-sm font-black text-slate-900 uppercase">Assign Users</h2>
                  <p className="text-[10px] text-slate-400">{selectedSurvey.title}</p>
                </div>
              </div>
              <button onClick={() => { setShowAssignModal(false); setUserSearchQuery(''); }} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
                <X size={18} />
              </button>
            </div>

            <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
              {/* Current Assignments */}
              <div>
                <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">
                  Current Assignments ({assignments.length})
                </h4>
                <div className="bg-slate-50 rounded-xl p-2 max-h-40 overflow-y-auto">
                  {assignments.length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-4">No users assigned yet</p>
                  ) : (
                    <div className="space-y-1">
                      {assignments.map(assignment => (
                        <div key={assignment.id} className="flex items-center justify-between p-2 bg-white rounded-lg">
                          <span className="text-sm font-bold text-slate-900">{assignment.user_name}</span>
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-bold uppercase rounded-md ${
                              assignment.is_completed
                                ? 'bg-emerald-50 text-emerald-600'
                                : 'bg-amber-50 text-amber-600'
                            }`}>
                              <span className={`w-1 h-1 rounded-full ${assignment.is_completed ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                              {assignment.is_completed ? 'Completed' : 'Pending'}
                            </span>
                            <button
                              onClick={() => handleRemoveAssignment(assignment.id)}
                              className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Add Users */}
              <div>
                <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Add Users</h4>

                {/* Search */}
                <div className="relative mb-2">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={userSearchQuery}
                    onChange={e => setUserSearchQuery(e.target.value)}
                    placeholder="Search users..."
                    className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                  {userSearchQuery && (
                    <button
                      onClick={() => setUserSearchQuery('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100 text-slate-400"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>

                {/* User List */}
                <div className="border border-slate-200 rounded-xl max-h-48 overflow-y-auto">
                  {availableUsers
                    .filter(u => !assignments.some(a => a.user === u.id))
                    .filter(u => {
                      if (!userSearchQuery) return true;
                      const query = userSearchQuery.toLowerCase();
                      const fullName = `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase();
                      const email = (u.email || '').toLowerCase();
                      return fullName.includes(query) || email.includes(query);
                    })
                    .map(u => (
                      <label
                        key={u.id}
                        className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer border-b border-slate-100 last:border-b-0 transition-colors ${
                          selectedUserIds.includes(u.id) ? 'bg-orange-50' : 'hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex-shrink-0">
                          {selectedUserIds.includes(u.id) ? (
                            <div className="w-4 h-4 rounded bg-orange-500 flex items-center justify-center">
                              <CheckCircle size={12} className="text-white" />
                            </div>
                          ) : (
                            <div className="w-4 h-4 border-2 border-slate-300 rounded" />
                          )}
                        </div>
                        <input
                          type="checkbox"
                          checked={selectedUserIds.includes(u.id)}
                          onChange={e => {
                            if (e.target.checked) {
                              setSelectedUserIds([...selectedUserIds, u.id]);
                            } else {
                              setSelectedUserIds(selectedUserIds.filter(id => id !== u.id));
                            }
                          }}
                          className="hidden"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-slate-900 truncate">{u.first_name || ''} {u.last_name || ''}</p>
                          {u.email && <p className="text-[10px] text-slate-500 truncate">{u.email}</p>}
                        </div>
                      </label>
                    ))}
                  {availableUsers
                    .filter(u => !assignments.some(a => a.user === u.id))
                    .filter(u => {
                      if (!userSearchQuery) return true;
                      const query = userSearchQuery.toLowerCase();
                      const fullName = `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase();
                      const email = (u.email || '').toLowerCase();
                      return fullName.includes(query) || email.includes(query);
                    }).length === 0 && (
                    <p className="text-xs text-slate-400 text-center py-4">No users found</p>
                  )}
                </div>
              </div>
            </div>

            <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 flex items-center justify-end gap-2">
              <button
                onClick={() => { setShowAssignModal(false); setUserSearchQuery(''); }}
                className="h-9 px-4 text-slate-600 rounded-xl hover:bg-slate-200 font-bold text-xs transition-colors"
              >
                Close
              </button>
              <button
                onClick={handleAssignUsers}
                disabled={selectedUserIds.length === 0}
                className="h-9 px-4 bg-orange-500 text-white rounded-xl hover:bg-orange-600 disabled:bg-slate-200 disabled:text-slate-400 font-bold text-xs uppercase tracking-wide transition-all shadow-lg shadow-orange-500/20 disabled:shadow-none"
              >
                Assign Selected ({selectedUserIds.length})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {showToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 bg-slate-900 text-white text-sm font-bold rounded-xl shadow-lg animate-[slideUp_0.2s_ease]">
          {toastMessage}
        </div>
      )}
    </div>
  );
};

export default SurveyList;
